import { useEffect, useRef, useState } from 'react';
import type { SearchSuggestion } from './search-panel';
import { PopularProduct, productSlugOf } from './search-popular';

// Matches /api/search-suggest's floor: single letters match almost everything.
export const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

export type SuggestionStatus = 'idle' | 'loading' | 'success' | 'error';

const fetchSuggestions = async (query: string, signal?: AbortSignal): Promise<SearchSuggestion[]> => {
  // Trailing slash matches next.config's trailingSlash, so the request isn't 308-redirected first.
  const res = await fetch(`/api/search-suggest/?q=${encodeURIComponent(query)}`, { signal });
  // A failed request is an error, not an empty result: the UI must not claim "no products match".
  if (!res.ok) throw new Error(`search-suggest responded ${res.status}`);
  const data: { items?: SearchSuggestion[] } = await res.json();
  return data.items ?? [];
};

/**
 * Typeahead for the header search. `status` describes the current query, so callers only say
 * "no products match" on a successful empty response, never while typing, debouncing or loading,
 * and not after a failure. While a new query loads, the previous results stay in `items` so the
 * panel doesn't flash empty between keystrokes.
 */
export const useSearchSuggestions = (query: string, active: boolean) => {
  const term = query.trim();
  const [items, setItems] = useState<SearchSuggestion[]>([]);
  const [status, setStatus] = useState<SuggestionStatus>('idle');

  useEffect(() => {
    if (!active || term.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setStatus('idle');
      return;
    }
    // Loading from the first keystroke, not only once the debounced request starts.
    setStatus('loading');
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchSuggestions(term, controller.signal)
        .then((results) => {
          setItems(results);
          setStatus('success');
        })
        .catch(() => {
          // An abort means a newer query took over and owns the status; anything else is a failure,
          // and the previous query's products go too, so they aren't shown as this query's results.
          if (controller.signal.aborted) return;
          setItems([]);
          setStatus('error');
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, active]);

  return { items, status };
};

/**
 * What the product area shows: search results once a query is long enough, otherwise the popular
 * products. Results replace the popular list outright (never mixed in), including while the first
 * results load or when nothing matches, so a popular product is never presented as a search result.
 */
export const selectPanelProducts = (query: string, suggestions: SearchSuggestion[], popular: SearchSuggestion[]) =>
  query.trim().length >= MIN_QUERY_LENGTH ? suggestions : popular;

/**
 * The curated popular products, shown straight from the hardwired `seed` so the panel is instant,
 * then refreshed against the catalog in the background the first time search opens. Each refresh
 * that finds the exact product replaces its seed entry (current price, image and link); one that
 * succeeds without a match drops the product (it's gone from the catalog). A failed lookup keeps the
 * seed entry and is retried on the next open; settled names are never re-requested.
 */
export const usePopularProducts = (seed: PopularProduct[], active: boolean) => {
  const [refreshed, setRefreshed] = useState<Record<string, SearchSuggestion | null>>({});
  // Names already tried during the current open. Reset on close, so a failure retries on the next
  // open instead of again straight away when another lookup settles and this effect re-runs.
  const attemptedThisOpen = useRef(new Set<string>());

  useEffect(() => {
    if (!active) {
      attemptedThisOpen.current.clear();
      return;
    }
    const pending = seed.filter(({ query }) => !(query in refreshed) && !attemptedThisOpen.current.has(query));
    pending.forEach(({ query, href }) => {
      attemptedThisOpen.current.add(query);
      fetchSuggestions(query)
        .then((results) => {
          const match = results.find((item) => productSlugOf(item.href) === productSlugOf(href));
          setRefreshed((current) => ({ ...current, [query]: match ?? null }));
        })
        .catch(() => {
          /* seed entry stays; the next open retries it */
        });
    });
  }, [active, seed, refreshed]);

  // In seed order: the refreshed entry when there is one, the seed until then, nothing once absent.
  return seed.flatMap((product) => {
    if (!(product.query in refreshed)) return [product];
    const current = refreshed[product.query];
    return current ? [current] : [];
  });
};
